import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationError } from "../../../models/errors/application-error";
import ProductService from "../../../services/product/product-service";
import {
  ApplicationProductList,
} from "../../../models/product/application-product-list";
import { ApplicationProductDetail } from "../../../models/product/application-product-detail";
import { ApplicationBestSupplierProduct } from "../../../models/product/application-best-supplier-product";

export const fetchProducts = createAsyncThunk<
  ApplicationProductList | null,
  number,
  { state: RootState }
>("products/fetch", async (subDepartmentId, { getState, rejectWithValue }) => {
  const state = getState();
  const productService = new ProductService();
  try {
    var currentPosition: number =
      state.product.products?.currentPosition ?? 0;
    var fetchNext: number = 70;
    var count: number = state.product.products?.count ?? -1;

    if (currentPosition !== count) {
      // change to just fetch all, but left code in here if we need to infitite scroll products in future.
      return await productService.getProductsBySubDepartments(
        state.branch.selectedBranch!.id,
        subDepartmentId,
        currentPosition,
        fetchNext,
        true
      );
    }

    return null;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const searchProducts = createAsyncThunk<
  ApplicationProductList | null,
  { subDepartmentId: number; searchText: string },
  { state: RootState }
>(
  "products/search",
  async ({ subDepartmentId, searchText }, { getState, rejectWithValue }) => {
    const state = getState();
    const productService = new ProductService();
    try {
      var currentPosition: number =
        state.product.searchResults?.currentPosition ?? 0;
      var fetchNext: number = 70;
      var count: number = state.product.searchResults?.count ?? -1;

      if (currentPosition != count) {
        return await productService.searchProducts(
          state.branch.selectedBranch!.id,
          subDepartmentId,
          searchText,
          currentPosition,
          fetchNext,
          false
        );
      }

      return null;
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

export const fetchProduct = createAsyncThunk<
  ApplicationProductDetail | null,
  number,
  { state: RootState }
>("product/fetch", async (productId, { getState, rejectWithValue }) => {
  const state = getState();
  const productService = new ProductService();
  try {
    return await productService.getProduct(
      productId,
      state.branch.selectedBranch!.id
    );
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchSupplierProduct = createAsyncThunk<
  ApplicationBestSupplierProduct | null,
  { productId: number, branchId: number },
  { state: RootState }
>(
  "product/supplier/fetch",
  async (
    { productId, branchId },
    { getState, rejectWithValue }) => {
    const state = getState();
    const productService = new ProductService();
    try {
      return await productService.getBestSupplierProduct(productId, branchId);
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

export const updateBranchProduct = createAsyncThunk<
  { productId: number; active: boolean },
  { productId: number; active: boolean },
  { state: RootState }
>(
  "product/branchProduct/update",
  async ({ productId, active }, { getState, rejectWithValue }) => {
    const state = getState();
    const productService = new ProductService();
    try {
      await productService.upsertBranchProduct(
        state.branch.selectedBranch!.id,
        productId,
        active
      );
      return { productId, active };
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

interface SetLoadingProductPayload {

  loading: boolean;
}

interface ProductState {
  products: ApplicationProductList | null;
  searchString: string;
  searchResults: ApplicationProductList | null;
  searchLoading: boolean;
}

const initialState: ProductState = {
  products: null,
  searchString: "",
  searchResults: null,
  searchLoading: false,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
      state.searchResults = initialState.searchResults;
    },
    resetProductPage: (state) => {
      state.products = null;
    },
    setLoadingProduct(state, action: PayloadAction<boolean>) {
      if (state.products) {
        state.products.loading = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.fulfilled, handleFetchProductsFulfilled)
      .addCase(searchProducts.fulfilled, handleSearchProductsFulfilled)
      .addCase(searchProducts.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchProducts.rejected, (state) => {
        state.searchLoading = false;
      })
      .addCase(updateBranchProduct.fulfilled, handleUpdateBranchProduct);
  },
});

const handleUpdateBranchProduct = (
  state: ProductState,
  action: PayloadAction<{
    productId: number;
    active: boolean;
  }>
) => {
  const { productId, active } = action.payload;

  if (!active) {
    // Function to remove a product by its ID
    const removeProductById = (
      products: ApplicationProductList,
      productId: number
    ) => {
      const productIndex = products.products.findIndex(
        (x) => x.id === productId
      );
      if (productIndex !== -1) {
        products.products.splice(productIndex, 1);
      }
    };

    if (state.searchResults) {
      removeProductById(state.searchResults, productId);
      state.searchResults.count -= 1;
    }
  }
};

const handleSearchProductsFulfilled = (
  state: ProductState,
  action: PayloadAction<ApplicationProductList | null>
) => {
  if (action.payload) {
    const { products, currentPosition, count } = action.payload;
    if (state.searchResults) {
      state.searchResults.products =
        state.searchResults.products.concat(products);
      state.searchResults.currentPosition = currentPosition;
      state.searchResults.count = count;
    } else {
      state.searchResults = action.payload;
    }
  }
  state.searchLoading = false;
};
const handleFetchProductsFulfilled = (
  state: ProductState,
  action: PayloadAction<ApplicationProductList | null>
) => {
  if (action.payload) {
    const { products, currentPosition, count } = action.payload;
    if (state.products) {
      state.products.products =
        state.products.products.concat(products);
      state.products.currentPosition = currentPosition;
      state.products.count = count;
    } else {
      state.products = action.payload;
    }
  }
};

export const {
  setLoadingProduct,
  resetProductPage,
  setSearchString,
} = productSlice.actions;
export default productSlice.reducer;
